import { createApi } from "@reduxjs/toolkit/query/react";

import { safeInvalidateTags } from "@/providers/store/api/safe-invalidate-tags";

import { ApiTags } from "../api-tags.enum";
import customFetchBase from "../custom-fetch-base";

import type {
  IAiGlossaryItem,
  IAiGlossaryDraftResponse,
  IAiGlossaryTestResponse,
  ICreateAiGlossaryItem,
  IUpdateAiGlossaryItem,
  ICreateGlobalAiGlossaryItem,
  IUpdateGlobalAiGlossaryItem,
} from "./ai-glossary.interface";

const aiGlossaryApi = createApi({
  reducerPath: "aiGlossaryApi",
  baseQuery: customFetchBase,
  tagTypes: [ApiTags.AiGlossary],
  endpoints: (builder) => ({
    getGlossaryItems: builder.query<IAiGlossaryItem[], { districtId: string; search?: string; category?: string; active?: boolean }>({
      query: ({ districtId, search, category, active }) => ({
        url: `/districts/${districtId}/ai-glossary`,
        method: "GET",
        params: { search, category, active },
      }),
      providesTags: safeInvalidateTags((result, error, { districtId }) => [{ type: ApiTags.AiGlossary, id: districtId }]),
      transformResponse: (result: { data: IAiGlossaryItem[] }) => result.data ?? [],
    }),

    generateDraft: builder.mutation<IAiGlossaryDraftResponse, { districtId: string; term: string; definition: string; notes?: string }>({
      query: ({ districtId, term, definition, notes }) => ({
        url: `/districts/${districtId}/ai-glossary/draft`,
        method: "POST",
        body: { term, definition, notes },
      }),
      transformResponse: (result: { data: IAiGlossaryDraftResponse }) => result.data,
    }),

    testUnderstanding: builder.mutation<IAiGlossaryTestResponse, { districtId: string; question: string; draftTerms?: { term: string; definition: string; aliases?: string[] }[] }>({
      query: ({ districtId, question, draftTerms }) => ({
        url: `/districts/${districtId}/ai-glossary/test`,
        method: "POST",
        body: { question, draftTerms },
      }),
      transformResponse: (result: { data: IAiGlossaryTestResponse }) => result.data,
    }),

    createGlossaryItem: builder.mutation<IAiGlossaryItem, ICreateAiGlossaryItem>({
      query: ({ districtId, ...body }) => ({
        url: `/districts/${districtId}/ai-glossary`,
        method: "POST",
        body,
      }),
      invalidatesTags: safeInvalidateTags((result, error, { districtId }) => [{ type: ApiTags.AiGlossary, id: districtId }]),
      transformResponse: (result: { data: IAiGlossaryItem }) => result.data,
    }),

    updateGlossaryItem: builder.mutation<IAiGlossaryItem, IUpdateAiGlossaryItem>({
      query: ({ districtId, id, ...body }) => ({
        url: `/districts/${districtId}/ai-glossary/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: safeInvalidateTags((result, error, { districtId }) => [{ type: ApiTags.AiGlossary, id: districtId }]),
      transformResponse: (result: { data: IAiGlossaryItem }) => result.data,
    }),

    deleteGlossaryItem: builder.mutation<void, { districtId: string; id: string }>({
      query: ({ districtId, id }) => ({
        url: `/districts/${districtId}/ai-glossary/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: safeInvalidateTags((result, error, { districtId }) => [{ type: ApiTags.AiGlossary, id: districtId }]),
    }),

    // Platform-level (admin) global glossary endpoints
    getGlobalGlossaryItems: builder.query<IAiGlossaryItem[], { search?: string; category?: string; active?: boolean }>({
      query: ({ search, category, active }) => ({
        url: `/admin/ai-glossary`,
        method: "GET",
        params: { search, category, active },
      }),
      providesTags: [{ type: ApiTags.AiGlossary, id: "__global__" }],
      transformResponse: (result: { data: IAiGlossaryItem[] }) => result.data ?? [],
    }),

    createGlobalGlossaryItem: builder.mutation<IAiGlossaryItem, ICreateGlobalAiGlossaryItem>({
      query: (body) => ({
        url: `/admin/ai-glossary`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: ApiTags.AiGlossary, id: "__global__" }],
      transformResponse: (result: { data: IAiGlossaryItem }) => result.data,
    }),

    updateGlobalGlossaryItem: builder.mutation<IAiGlossaryItem, IUpdateGlobalAiGlossaryItem>({
      query: ({ id, ...body }) => ({
        url: `/admin/ai-glossary/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [{ type: ApiTags.AiGlossary, id: "__global__" }],
      transformResponse: (result: { data: IAiGlossaryItem }) => result.data,
    }),

    deleteGlobalGlossaryItem: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `/admin/ai-glossary/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: ApiTags.AiGlossary, id: "__global__" }],
    }),

    generateGlobalDraft: builder.mutation<IAiGlossaryDraftResponse, { term: string; definition: string; notes?: string }>({
      query: ({ term, definition, notes }) => ({
        url: `/admin/ai-glossary/draft`,
        method: "POST",
        body: { term, definition, notes },
      }),
      transformResponse: (result: { data: IAiGlossaryDraftResponse }) => result.data,
    }),

    testGlobalUnderstanding: builder.mutation<IAiGlossaryTestResponse, { question: string; draftTerms?: { term: string; definition: string; aliases?: string[] }[] }>({
      query: ({ question, draftTerms }) => ({
        url: `/admin/ai-glossary/test`,
        method: "POST",
        body: { question, draftTerms },
      }),
      transformResponse: (result: { data: IAiGlossaryTestResponse }) => result.data,
    }),
  }),
});

export const {
  useGetGlossaryItemsQuery,
  useGenerateDraftMutation,
  useTestUnderstandingMutation,
  useCreateGlossaryItemMutation,
  useUpdateGlossaryItemMutation,
  useDeleteGlossaryItemMutation,
  useGetGlobalGlossaryItemsQuery,
  useCreateGlobalGlossaryItemMutation,
  useUpdateGlobalGlossaryItemMutation,
  useDeleteGlobalGlossaryItemMutation,
  useGenerateGlobalDraftMutation,
  useTestGlobalUnderstandingMutation,
} = aiGlossaryApi;

export default aiGlossaryApi;
